import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal } from '@prio365/prio365-react-library';
import { Badge, Dropdown, Menu, notification } from 'antd';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DroppableElement from '../../../../dnd/components/DroppableElement';
import {
  DND_TYPE_DRIVE_ITEM_FILE,
  DND_TYPE_EMAIL,
  DndDriveItemDto,
  DndEmailDto,
} from '../../../../dnd/types';
import { setSiderSetting } from '../../../userSettings/actions/themeSettings/sider';
import CreateSubprojectModal from '../Modals/CreateSubprojectModal';

import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';
import { DropTargetMonitor } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import {
  getActiveMailFolderId,
  getActiveProject,
  getUserMe,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import { Project, ProjectNewsCounts } from '../../../../models/Project';
import {
  MailFolderId,
  ProjectId,
  ProjectTabViews,
} from '../../../../models/Types';
import { PrioTheme } from '../../../../theme/types';
import { isDriveItemFolder } from '../../../documents/util';
import { assigneeReadMessageProject } from '../../../mail/actions/projects/messages';
import {
  addToProjectFavorites,
  deleteProjectNewsByProjectId,
  fetchProjectById,
  leaveProjectMe,
  removeFromProjectFavorites,
  setActiveProjectTabView,
  updateInternalProjectContactMeNotification,
} from '../../actions';
import {
  apiArchiveProject,
  apiFetchProjectMeNotification,
  apiLeaveProjectMe,
  apiUnarchiveProject,
  apiUpdateProjectMeNotification,
} from '../../api';
import { getFavoriteProjectsLength } from '../../selectors';
import { ProjectDropDownNavigationRefProps } from './ProjectDropDownNavigation';
import useProjectNewsContext from '../../hooks/useProjectNewsContext';

const CLASSNAME = 'prio-navigation-panel-header-project';

interface NavigationItemPanelHeaderProjectProps {
  ref?: React.ForwardedRef<ProjectDropDownNavigationRefProps>;
  project: Project;
  rootPath: string;
  projectReady: boolean;
  isProjectMember: boolean;
  isSubMenu?: boolean;
  type?: ProjectTabViews;
  isActive: boolean;
  isParentProject?: boolean;
  collapsed?: boolean;
  subProjects?: {
    [projectId: string]: string[];
  };
  setCollapsed?: (projectId: ProjectId, collapsed: boolean) => void;
  isAnySubProjectFavorited?: boolean;
  activeTab?: string;
  rerenderProjectList?: () => void;
  projectNewsCounts: ProjectNewsCounts;
}

export const NavigationItemPanelHeaderProject: React.FC<
  NavigationItemPanelHeaderProjectProps
> = (props) => {
  //#region ------------------------------ Defaults
  const theme = useTheme<PrioTheme>();
  const {
    project,
    rootPath,
    projectReady,
    isProjectMember,
    isSubMenu,
    type,
    isActive,
    isParentProject,
    collapsed,
    subProjects,
    setCollapsed,
    isAnySubProjectFavorited,
    activeTab,
    rerenderProjectList,
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const activeProjectId = useSelector(getActiveProject);
  const [isNotificationsFetching, setIsNotificationsFetching] =
    useState<boolean>(false);
  const [notificationEnabled, setNotificationEnabled] =
    useState<boolean>(false);
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const [createSubprojectModalVisible, setCreateSubprojectModalVisible] =
    useState<boolean>(false);
  const [isProjectLeaveModalVisible, setIsProjectLeaveModalVisible] =
    useState<boolean>(false);

  const userMe = useSelector(getUserMe);
  const isOfficeAssistance =
    userMe?.prioData.officeRoles?.[userMe.myOfficeId]?.includes(
      'officeAssistance'
    ) || false;

  const isGlobalAssistance =
    userMe?.prioData.globalRoles?.includes('globalAssistance') || false;

  const activeMailFolderId = useSelector<RootReducerState, MailFolderId>(
    (state) => getActiveMailFolderId(state, project?.projectId)
  );
  const favoriteProjectsLength = useSelector(getFavoriteProjectsLength);

  const chevronVisible =
    isParentProject &&
    (activeTab === 'favorites' ? isAnySubProjectFavorited : true);

  const [projectIsArchived, setProjectIsArchived] = useState<boolean>(
    project?.isArchived
  );

  const { getProjectNewsByProjectId } = useProjectNewsContext();

  const hasMailAccess = useMemo(() => {
    if (activeTab === 'all') {
      return project.sharedMailboxUserId;
    }
    return project?.projectExtensionMeDtos?.some(
      ({ projectExtensionType, extensionUserState }) =>
        projectExtensionType === 'sharedMailbox' &&
        extensionUserState === 'ready'
    );
  }, [activeTab, project]);

  const markMessagesAssignmentsAsRead = useCallback(() => {
    getProjectNewsByProjectId(
      project.projectId
    )?.messageAssignmentUnread?.forEach((news) => {
      dispatch(
        assigneeReadMessageProject(project?.projectId, news.referenceObjectId)
      );
    });
  }, [dispatch, project?.projectId, getProjectNewsByProjectId]);

  const projectHasSharepointAccess = useMemo(() => {
    return (
      project.projectExtensionMeDtos?.some(
        ({ projectExtensionType, extensionUserState }) =>
          projectExtensionType === 'sharepoint' &&
          extensionUserState === 'ready'
      ) ??
      (project.groupId !== undefined && project.groupId !== null)
    );
  }, [project.projectExtensionMeDtos, project.groupId]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const subProjectHasProjectNews = useMemo(() => {
    const currentProjectSubProjects = subProjects?.[project.projectId] ?? [];
    return (
      isParentProject &&
      currentProjectSubProjects.some(
        (subProject) =>
          getProjectNewsByProjectId(subProject)?.['messageAssignmentUnread']
            ?.length > 0
      )
    );
  }, [isParentProject, subProjects, project, getProjectNewsByProjectId]);

  const projectHasProjectNews = useMemo(() => {
    return (
      getProjectNewsByProjectId(project.projectId)?.['messageAssignmentUnread']
        ?.length > 0
    );
  }, [project.projectId, getProjectNewsByProjectId]);

  const fetchNotificationMe = useCallback(async () => {
    setIsNotificationsFetching(true);
    const { result, data } = await apiFetchProjectMeNotification(
      project.projectId
    );
    setIsNotificationsFetching(false);
    if (result.status >= 200 && result.status < 300) {
      setNotificationEnabled(data);
    }
  }, [project]);

  const updateNotificationMe = useCallback(async () => {
    setIsNotificationsFetching(true);
    const { data } = await apiUpdateProjectMeNotification(
      project.projectId,
      !notificationEnabled
    );
    setIsNotificationsFetching(false);
    if (data) {
      dispatch(
        updateInternalProjectContactMeNotification(project.projectId, data)
      );
      setNotificationEnabled(data.emailNotificationEnabled);
    } else {
      notification.open({
        message: t('common:error'),
        description: notificationEnabled
          ? t('projects:errorMessages.deactivateNotification')
          : t('projects:errorMessages.activateNotification'),
      });
    }
  }, [project, notificationEnabled, dispatch, t]);

  const onDriveItemIntoProjectDrop = (
    item: unknown,
    monitor: DropTargetMonitor<unknown, unknown>,
    target: Project
  ) => {
    if (monitor.didDrop()) {
      return undefined;
    }
    const dndType = monitor.getItemType();
    switch (dndType) {
      case DND_TYPE_EMAIL: {
        if (
          !project.projectExtensionMeDtos.some(
            ({ projectExtensionType }) =>
              projectExtensionType === 'sharedMailbox'
          )
        ) {
          return undefined;
        }

        const convertedItem = item as DndEmailDto;
        if (isProjectMember) {
          return {
            projectId: project.projectId,
            message: convertedItem.message,
            showModal: true,
          };
        }
        return undefined;
      }
      case DND_TYPE_DRIVE_ITEM_FILE: {
        // const convertedItem = item as DndDriveItemDto & {
        //   projectId: ProjectId;
        // };
        if (isProjectMember && projectHasSharepointAccess) {
          return {
            type: 'project',
            object: target,
          };
          // return {
          //   ...convertedItem,
          //   project: project,
          //   showDrawer: true,
          //   dropTargetType: 'project',
          //   destinationProject: project,
          //   destinationDriveItemId: `root-group-${project.groupId}`,
          //   destinationDriveItemIsRoot: true,
          // };
        }
        return undefined;
      }
      default: {
        return undefined;
      }
    }
  };

  const handleDisableOnDropOver: (
    item: DndEmailDto & DndDriveItemDto & { projectId: ProjectId },
    monitor: DropTargetMonitor<unknown, unknown>,
    object,
    isOverCurrent: boolean
  ) => boolean = (item, monitor) => {
    const dndType = monitor.getItemType();
    switch (dndType) {
      case DND_TYPE_EMAIL: {
        const draggedItemId = item.message?.id;
        const messageInSelection = item.selectedMessages.find(
          (mes) => mes.id === draggedItemId
        );
        if (messageInSelection) {
          return !(
            isProjectMember &&
            item.selectedMessages.filter(
              (mes) =>
                !mes.meetingMessageType || mes.meetingMessageType === 'none'
            ).length > 0 &&
            project.projectExtensionMeDtos.some(
              ({ projectExtensionType }) =>
                projectExtensionType === 'sharedMailbox'
            )
          );
        }
        return !(
          isProjectMember &&
          (!item.message?.meetingMessageType ||
            item.message?.meetingMessageType === 'none') &&
          project.projectExtensionMeDtos.some(
            ({ projectExtensionType }) =>
              projectExtensionType === 'sharedMailbox'
          )
        );
      }
      case DND_TYPE_DRIVE_ITEM_FILE: {
        return !(
          isProjectMember &&
          projectHasSharepointAccess &&
          !(item.driveItems ?? [item.driveItem]).find((dItem) =>
            isDriveItemFolder(dItem)
          ) &&
          item.sourceProjectId !== project.projectId
        );
      }
      default: {
        return !isProjectMember;
      }
    }
  };

  const handleProjectLeave = async () => {
    setIsProjectLeaveModalVisible(false);
    if (project?.projectId) {
      const { result, data } = await apiLeaveProjectMe(project.projectId);
      if (result.status >= 200 && result.status < 300) {
        dispatch(leaveProjectMe(project?.projectId));

        if (activeProjectId === project?.projectId)
          navigate('/module/prio/projects/me/dashboard/');
      } else {
        notification.open({
          message: t('common:error'),
          description: data?.TranslatedMessage,
        });
      }
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    setProjectIsArchived(project?.isArchived);
  }, [project]);
  //#endregion

  //#region ------------------------------ Component
  const menu = useMemo(() => {
    const handleProjectArchive = async () => {
      const result = projectIsArchived
        ? await apiUnarchiveProject(project.projectId)
        : await apiArchiveProject(project.projectId);
      const status = result.status;

      if (status >= 200 && status < 300) {
        setProjectIsArchived(!projectIsArchived);
        dispatch(fetchProjectById(project.projectId, isProjectMember));
        rerenderProjectList();
      } else {
        notification.open({
          message: t('common:error'),
          description: t(
            `projects:errorMessages.${
              projectIsArchived ? 'unarchive' : 'archive'
            }Project`
          ),
        });
      }
    };

    return (
      <Menu>
        {projectHasProjectNews && (
          <Menu.Item
            onClick={(e) => {
              e.domEvent.stopPropagation();
              markMessagesAssignmentsAsRead();
              dispatch(deleteProjectNewsByProjectId(project.projectId));
            }}
          >
            {t('projects:projectNews.resetProjectNews')}
          </Menu.Item>
        )}
        {!projectIsArchived && isProjectMember && (
          <Menu.Item
            onClick={(e) => {
              e.domEvent.stopPropagation();
              if (!project.favorite) {
                dispatch(setActiveProjectTabView(type));
                dispatch(addToProjectFavorites(project.projectId));
              } else {
                dispatch(removeFromProjectFavorites(project.projectId));
              }
            }}
            disabled={!project.favorite && (favoriteProjectsLength ?? 0) >= 15}
          >
            {t(
              `projects:navigation.dropDownMenu.${
                project.favorite ? 'removeFrom' : 'addTo'
              }Favorites`,
              {
                favoriteLength: favoriteProjectsLength ?? 0,
              }
            )}
          </Menu.Item>
        )}
        {!projectIsArchived && isProjectMember && (
          <Menu.Item
            onClick={(e) => {
              e.domEvent.stopPropagation();
              updateNotificationMe();
            }}
            disabled={isNotificationsFetching}
            className={`${CLASSNAME}-notifications`}
          >
            {t(
              `projects:navigation.dropDownMenu.${
                notificationEnabled
                  ? 'deactivateNotifications'
                  : 'activateNotifications'
              }`
            )}
            <div className={`${CLASSNAME}-notificationLoadingCell`}>
              {isNotificationsFetching && <LoadingOutlined />}
            </div>
          </Menu.Item>
        )}
        {!project.parentProject && !projectIsArchived && (
          <Menu.Item
            onClick={(e) => {
              e.domEvent.stopPropagation();
              setCreateSubprojectModalVisible(true);
            }}
            disabled={project.isTemporary}
          >
            {t('projects:createSubprojectModal.menuTitle')}
          </Menu.Item>
        )}
        <Menu.Item
          onClick={(e) => {
            e.domEvent.stopPropagation();
            handleProjectArchive();
            dispatch(removeFromProjectFavorites(project.projectId));
          }}
          disabled={project.isTemporary}
        >
          {t(
            `projects:navigation.dropDownMenu.${
              projectIsArchived ? 'unarchiveProject' : 'archiveProject'
            }`
          )}
        </Menu.Item>
        {isProjectMember && (
          <Menu.Item
            onClick={(e) => {
              e.domEvent.stopPropagation();
              setIsProjectLeaveModalVisible(true);
            }}
            disabled={project.isTemporary}
          >
            {t(`projects:navigation.dropDownMenu.leaveProject`)}
          </Menu.Item>
        )}
      </Menu>
    );
  }, [
    project,
    isNotificationsFetching,
    notificationEnabled,
    type,
    favoriteProjectsLength,
    projectIsArchived,
    isProjectMember,
    updateNotificationMe,
    rerenderProjectList,
    t,
    dispatch,
    markMessagesAssignmentsAsRead,
    projectHasProjectNews,
  ]);
  //#endregion

  return (
    <>
      <DroppableElement
        activeOverlay
        accept={[DND_TYPE_EMAIL, DND_TYPE_DRIVE_ITEM_FILE]}
        onDrop={onDriveItemIntoProjectDrop}
        object={project}
        disable={handleDisableOnDropOver}
      >
        <div
          className={classNames(`${CLASSNAME}-panelHeaderRoot`, {
            [`${CLASSNAME}-panelHeaderRootSubMenu`]: isSubMenu,
            [`${CLASSNAME}-panelHeaderRootSubMenuActive`]:
              isSubMenu && isActive,
          })}
          onClick={() => {
            if (isSubMenu) {
              dispatch(setSiderSetting({ subMenuState: 'closed' }));
            }
          }}
        >
          <div
            style={{ display: 'flex', flexDirection: 'row' }}
            className={`${CLASSNAME}-headerRow`}
          >
            {!!project.parentProject && (
              <FontAwesomeIcon
                size="sm"
                icon={['fal', 'horizontal-rule']}
                className={`${CLASSNAME}-icon`}
              />
            )}
            <Link
              to={`${rootPath}${project.projectId}${
                isProjectMember && projectReady && hasMailAccess
                  ? `/mail${activeMailFolderId ? `/${activeMailFolderId}` : ''}`
                  : '/summary'
              }`}
              className={classNames(
                `${CLASSNAME}-flex1`,
                `${CLASSNAME}-oneLine`,
                {
                  [`${CLASSNAME}-notReady`]: !projectReady,
                  [`${CLASSNAME}-activeLink`]: isActive,
                }
              )}
            >
              <div
                className={
                  projectIsArchived ? `${CLASSNAME}-archivedProject` : null
                }
              >{`${project.number} ${project.shortName ?? project.name}`}</div>
            </Link>
            {collapsed && subProjectHasProjectNews ? (
              <Badge
                className={`${CLASSNAME}-badge`}
                dot
                color={theme.colors.base.blue[80]}
              ></Badge>
            ) : null}
            {projectHasProjectNews ? (
              <Badge
                className={`${CLASSNAME}-badge`}
                dot
                color={theme.old.palette.primaryColor}
              ></Badge>
            ) : null}
            {(isProjectMember || isOfficeAssistance || isGlobalAssistance) && (
              <Dropdown
                onVisibleChange={setDropdownVisible}
                overlay={menu}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  iconProp={['fal', 'ellipsis-v']}
                  className={classNames(
                    'prio-project-navigation-context-menu-button',
                    {
                      [`${CLASSNAME}-menuButtonWithoutCollapse`]:
                        !isParentProject,
                    }
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!dropdownVisible) {
                      fetchNotificationMe();
                    }
                  }}
                  type="default"
                />
              </Dropdown>
            )}
            {chevronVisible && (
              <Button
                className="prio-project-navigation-chevron-button"
                type="default"
                iconProp={['fal', collapsed ? 'angle-down' : 'angle-up']}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCollapsed(project.projectId, !collapsed);
                }}
              />
            )}
          </div>
        </div>
        {!project.parentProject && createSubprojectModalVisible && (
          <CreateSubprojectModal
            visible={createSubprojectModalVisible}
            project={project}
            onClose={() => setCreateSubprojectModalVisible(false)}
          />
        )}
      </DroppableElement>
      {isProjectLeaveModalVisible && (
        <Modal
          title={t('projects:leaveProjectMeModal.title')}
          visible={isProjectLeaveModalVisible}
          onOk={handleProjectLeave}
          onClose={() => setIsProjectLeaveModalVisible(false)}
          okText={t('common:actions.confirm')}
          cancelText={t('common:actions.cancel')}
        >
          {t('projects:leaveProjectMeModal.content')}{' '}
        </Modal>
      )}
    </>
  );
};

export default NavigationItemPanelHeaderProject;
